import React, { createContext, useContext, useCallback, useMemo } from 'react';
import {
  MsalProvider,
  useMsal,
  useIsAuthenticated,
  useAccount,
  useMsalAuthentication,
} from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { msalInstance, loginRequest } from './authConfig';

// ── Dev bypass flag ──────────────────────────────────────────────────────────

const AUTH_ENABLED = process.env.REACT_APP_AUTH_ENABLED !== 'false';

// ── Auth context ─────────────────────────────────────────────────────────────

const AuthContext = createContext(null);

/**
 * Hook to access auth state and actions anywhere in the app.
 *
 * Returns: { user, roles, isAuthenticated, login, logout, getToken, hasRole, displayRole }
 */
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
};

// ── Dev-mode provider (no MSAL, mock admin user) ─────────────────────────────

const DevAuthProvider = ({ children }) => {
  const value = useMemo(
    () => ({
      user: { name: 'Dev User', firstName: 'Dev', email: 'dev@localhost', initials: 'DU' },
      roles: ['Admin', 'Supervisor', 'Analyst'],
      displayRole: 'Admin',
      isAuthenticated: true,
      login: async () => {},
      logout: async () => { window.location.href = '/login'; },
      getToken: async () => null,
      hasRole: () => true,
    }),
    [],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ── MSAL provider (production) ───────────────────────────────────────────────

const MsalAuthContextProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] ?? {});

  const user = useMemo(() => {
    if (!account) return null;
    const claims = account.idTokenClaims || {};
    const name = account.name || account.username || '';
    const email = claims.email || account.username || '';
    // Prefer the `given_name` claim. Fall back to splitting the display name,
    // handling "Last, First" and plain "First Last".
    const firstName = claims.given_name
      || (name.includes(',') ? name.split(',')[1]?.trim() : name.split(' ')[0])
      || name;
    const initials = name
      .split(/[\s,]+/)
      .filter(Boolean)
      .map((p) => p[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    return { name, firstName, email, initials };
  }, [account]);

  const roles = useMemo(() => {
    if (!account?.idTokenClaims?.roles) return [];
    return account.idTokenClaims.roles;
  }, [account]);

  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error('[LISA Auth] Login failed:', err);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({ postLogoutRedirectUri: '/' });
    } catch (err) {
      console.error('[LISA Auth] Logout failed:', err);
    }
  }, [instance]);

  const getToken = useCallback(async () => {
    if (!account) return null;
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(loginRequest);
        return null;
      }
      console.error('[LISA Auth] Token acquisition failed:', err);
      return null;
    }
  }, [instance, account]);

  const displayRole = useMemo(() => {
    for (const role of ['Admin', 'Supervisor', 'Analyst']) {
      if (roles.includes(role)) return role;
    }
    return 'User';
  }, [roles]);

  const hasRole = useCallback(
    (...required) => required.some((r) => roles.includes(r)),
    [roles],
  );

  const isLoading = inProgress !== InteractionStatus.None;

  const value = useMemo(
    () => ({
      user,
      roles,
      displayRole,
      isAuthenticated,
      isLoading,
      login,
      logout,
      getToken,
      hasRole,
    }),
    [user, roles, displayRole, isAuthenticated, isLoading, login, logout, getToken, hasRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ── Outer provider ───────────────────────────────────────────────────────────

const AuthProvider = ({ children }) => {
  // Dev mode: skip MSAL entirely
  if (!AUTH_ENABLED) {
    return <DevAuthProvider>{children}</DevAuthProvider>;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <MsalAuthContextProvider>{children}</MsalAuthContextProvider>
    </MsalProvider>
  );
};

export default AuthProvider;
